import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-tech-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 tech-grid opacity-30" />
      
      {/* Glow Effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-tech-accent/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-tech-accent/3 rounded-full blur-3xl" />
      
      {/* Scan Line */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-tech-accent/20 to-transparent animate-scan" />
      </div>

      <div className="relative z-10 max-w-sm w-full">
        {/* Logo & Title */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded bg-tech-surface border border-tech-border mb-4 glow-accent-sm">
            <svg className="w-8 h-8 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.98 10.804V12.18c0 1.128.48 2.11 1.237 2.747l2.963 2.478c.77.646.77 1.882 0 2.528L19.7 21.02c-.757.637-1.237 1.62-1.237 2.748v1.436" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.02 13.196V11.82c0-1.128-.48-2.11-1.237-2.747L.82 6.595c-.77-.646-.77-1.882 0-2.528L4.298 1.88c.757-.637 1.237-1.62 1.237-2.748V.982" />
            </svg>
          </div>
          <h1 className="text-2xl font-mono font-semibold text-tech-text tracking-tight">XBASE</h1>
          <p className="text-tech-textMuted text-sm mt-1 font-mono">IDENTITY MANAGEMENT SYSTEM</p>
        </div>

        {/* Login Form */}
        <div className="tech-card p-6 animate-slide-left" style={{ animationDelay: '0.1s' }}>
          <div className="mb-5">
            <span className="tech-section-title">CREDENTIALS</span>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-tech-danger/10 border border-tech-danger/30 rounded text-tech-danger text-sm font-mono flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-xs font-mono text-tech-textMuted mb-2">
                USERNAME
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="tech-input"
                placeholder="enter username"
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-mono text-tech-textMuted mb-2">
                PASSWORD
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="tech-input"
                placeholder="enter password"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full tech-btn-primary py-3 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  AUTHENTICATING
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  ACCESS SYSTEM
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-tech-border">
            <p className="text-center text-sm text-tech-textMuted">
              No account?{' '}
              <Link to="/register" className="text-tech-accent hover:text-tech-accentHover transition">
                REGISTER
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-tech-textDim text-xs font-mono">SYS.v2.0.0 // SECURE CONNECTION</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
